import { clsx } from 'clsx'
import * as React from 'react'
import {
  ArrowTopRightIcon,
  ChevronDownIcon,
  Cross2Icon,
} from '@radix-ui/react-icons'
import {
  useFloating,
  flip,
  shift,
  inline,
  autoUpdate,
  offset,
} from '@floating-ui/react'

import {
  type ComboboxOption,
  useComboboxState,
} from '~/hooks/use-combobox-state'
import { IconButton } from './icon-button'
import { ScrollArea } from './scroll-area'
import { ButtonLink } from './button'
import { BlockIcon } from '~/components/block-icon'
import { Block, isBlockWithChildren } from '@basehub/data/replicache/index'
import {
  RepoStore,
  RepoStoreFocusType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { ArrowTopRightSmallIcon } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/reference/input'
import { useTabsStoreRoot } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { useEditorMode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-mode-provider'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { Slot } from '@radix-ui/react-slot'

export type SelectorChildrenProps<OptionType extends ComboboxOption> = {
  options: OptionType[]
  getOptionProps: (index: number) => Record<string, unknown>
  selectedIndex?: number
  search: string
  listRef: React.RefObject<null | HTMLDivElement>
  scrollerElement: HTMLDivElement | null
}

export type SelectorProps<OptionType extends ComboboxOption> = {
  options: (search: string) => OptionType[]
  renderResult: (search: string) => React.ReactNode
  onSelect: (selectedOption: OptionType) => unknown
  children?: (data: SelectorChildrenProps<OptionType>) => React.ReactNode
  onRemove?: () => Promise<void> | void
  onFocus?: () => unknown
  onBlur?: () => unknown
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => Promise<void> | void
  closeAfterSelect?: boolean
  readOnly?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  id: string
  className?: string
  inputClassName?: string
  autoFocus?: boolean
  onPopoverClose?: () => unknown
  onPopoverOpen?: () => unknown
  expandableMode?: {
    toggle: () => unknown
    children: React.ReactNode
  }
  size?: 'sm' | 'md'
  placeholder?: string
  sideOffset?: number
  extraWidth?: number
  showCreateNew?: boolean
  search: string
  setSearch: (search: string) => unknown
}

const Selector = <OptionType extends ComboboxOption>({
  search,
  setSearch,
  options,
  renderResult,
  children,
  onSelect,
  onRemove,
  onFocus,
  onBlur,
  onKeyDown,
  className,
  inputClassName,
  closeAfterSelect = true,
  readOnly = false,
  disabled,
  icon = (
    <span className="size-6 pointer-events-none flex items-center justify-center">
      <ChevronDownIcon width={16} height={16} />
    </span>
  ),
  id,
  autoFocus,
  onPopoverClose,
  onPopoverOpen,
  expandableMode,
  size = 'md',
  placeholder,
  sideOffset = -2,
  extraWidth = 3,
  showCreateNew = true,
}: SelectorProps<OptionType>) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const inputContainerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const editorMode = useEditorMode()
  let {
    listRef,
    toggleState: { isOn, handleOn, handleOff, handleToggle },
    filteredOptions,
    selectedIndex,
    getOptionProps,
    onInputKeyDown,
    onSearch,
  } = useComboboxState<HTMLDivElement, OptionType>({
    options: options(search),
    onSelect: (selectedOption) => {
      onSelect(selectedOption)
      setSearch('')
      if (!closeAfterSelect) return
      handleOff()
    },
    selectedOptionDataAtrribute: 'data-combobox-item',
    onClose: onPopoverClose,
    onOpen: onPopoverOpen,
  })

  if (disabled) isOn = false

  const floating = useFloating({
    placement: 'bottom',
    open: true,
    onOpenChange: (open) => {
      if (!open) {
        handleOff()
        setSearch('')
      } else {
        handleOn()
      }
    },
    middleware: [inline(), flip(), shift(), offset({ mainAxis: sideOffset })],
    whileElementsMounted: autoUpdate,
    elements: {
      // eslint-disable-next-line react-compiler/react-compiler
      reference: inputContainerRef.current,
      // eslint-disable-next-line react-compiler/react-compiler
      floating: listRef.current,
    },
  })

  React.useEffect(() => {
    if (!isFocused) {
      handleOff()
      setSearch('')
    }
  }, [isFocused, handleOff, setSearch])

  React.useEffect(() => {
    if (isOn && inputRef.current) inputRef.current.focus()
  }, [isOn])

  React.useEffect(() => {
    if (autoFocus) handleOn()
  }, [autoFocus, handleOn])

  const isReadOnly = readOnly || editorMode === 'read'
  const [scrollerElement, setScrollerElement] =
    React.useState<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!listRef.current) return

    const scroller = listRef.current?.querySelector<HTMLDivElement>(
      '[data-radix-scroll-area-viewport]'
    )
    if (!scroller) return
    setScrollerElement(scroller)
  }, [listRef, setScrollerElement])

  if (!showCreateNew) return null

  return (
    <>
      <div
        className={clsx(
          'relative rounded-lg border overflow-hidden',
          className,
          isOn
            ? 'border-dark-control-base'
            : 'border-dark-border-solid focus-within:!border-dark-control-base enabled:hover:border-dark-elevated',
          disabled &&
            'cursor-not-allowed [&_*]:cursor-not-allowed [&_input]:!cursor-not-allowed'
        )}
      >
        <div
          className={clsx(
            {
              'min-h-[1.875rem] text-xs': size === 'sm',
              'text-sm min-h-[39px]': size === 'md',
            },
            'relative group/root select-none overflow-hidden !py-0 !pl-0 bg-dark-shade cursor-text flex flex-col'
          )}
          ref={inputContainerRef}
          onFocus={() => {
            if (onFocus) onFocus()
            setIsFocused(true)
          }}
          onBlur={() => {
            if (onBlur) onBlur()
            setIsFocused(false)
          }}
        >
          <div
            onClick={handleOn}
            className={clsx(
              isOn ? 'pointer-events-none' : 'flex-grow',
              'flex shrink-0 flex-1 items-center overflow-hidden'
            )}
          >
            <div
              className={clsx(
                inputClassName,
                {
                  'py-1 pl-1.5 pr-10': size === 'sm',
                  'py-[11px] pl-3 pr-18': size === 'md',
                },
                'gap-x-1.5 gap-y-2.5 flex flex-wrap items-center flex-1 h-full'
              )}
            >
              {renderResult(search)}
              <input
                ref={inputRef}
                autoComplete="off"
                id={id}
                placeholder={placeholder}
                data-selector-search-input
                readOnly={isReadOnly}
                autoFocus={isOn || autoFocus}
                disabled={disabled}
                className={clsx(
                  {
                    'text-xs': size === 'sm',
                    'text-sm': size === 'md',
                  },
                  'max-h-[15px] bg-transparent focus:outline-none flex-1 min-w-0 placeholder:text-dark-faint [&:not(:focus)]:truncate'
                )}
                value={search}
                onKeyDown={(e) => {
                  onInputKeyDown(e)
                  if (onKeyDown) onKeyDown(e)
                }}
                onClick={handleOn}
                onFocus={handleOn}
                onBlur={(e) => {
                  setSearch('')
                  e.target.value = ''
                  handleOff()
                }}
                onChange={(e) => {
                  setSearch(e.target.value)
                  onSearch(e.target.value)
                }}
              />
            </div>
          </div>

          <div
            className={clsx(
              { 'right-[3px]': size === 'sm' },
              { 'right-[7px]': size === 'md' },
              'absolute flex items-center top-1/2 -translate-y-1/2'
            )}
          >
            {onRemove && !disabled && (
              <IconButton onClick={onRemove} tabIndex={-1}>
                <Cross2Icon
                  className={clsx(
                    { 'size-3': size === 'sm' },
                    { 'size-4': size === 'md' },
                    'flex-1'
                  )}
                />
              </IconButton>
            )}
            {/* Only works if { icon } doesn't have pointer-events-none */}
            <Slot
              onPointerDown={(e) => {
                if (disabled) return
                e.stopPropagation()
                e.preventDefault()
                handleToggle()
              }}
            >
              {icon}
            </Slot>
          </div>
        </div>

        {expandableMode && expandableMode.children}
      </div>

      <div
        ref={listRef}
        style={{
          ...floating.floatingStyles,
          display: isOn ? 'block' : 'none',
          // eslint-disable-next-line react-compiler/react-compiler
          width: inputContainerRef.current?.clientWidth
            ? inputContainerRef.current.clientWidth + extraWidth
            : undefined,
          zIndex: 1000,
        }}
      >
        <SelectorList>
          {isOn &&
            children &&
            children({
              options: filteredOptions,
              getOptionProps,
              selectedIndex,
              search,
              listRef,
              scrollerElement,
            })}
        </SelectorList>
      </div>
    </>
  )
}

export const SelectorItem: React.FC<
  React.PropsWithChildren<
    React.JSX.IntrinsicElements['li'] & { className?: string }
  >
> = ({ className = '', children, ...rest }) => {
  return (
    <li {...rest} onMouseDown={(e) => e.preventDefault()} className={className}>
      {children}
    </li>
  )
}

export const SelectorEmptyState: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <p className="flex h-16 items-center justify-center p-1 text-center text-xs text-dark-dim">
      {children}
    </p>
  )
}

const SelectorList: React.FC<React.PropsWithChildren> = ({ children }) => (
  <ScrollArea
    className="shadow-2xl rounded-lg border border-dark-border-solid bg-dark-base pb-1 shadow-modal"
    asChild
  >
    <ul className="w-full px-1 pt-1" style={{ maxHeight: 284 }}>
      {children}
    </ul>
  </ScrollArea>
)

export const SelectorResultBlock = ({
  search,
  multiple,
  targetBlocks,
  emptyStateLabel,
  teamSlug,
  repoSlug,
  repoStore,
  onSelect,
}: {
  search: string
  multiple: boolean
  targetBlocks: Block[]
  emptyStateLabel?: string
  teamSlug: string | null
  repoSlug: string | null
  repoStore: Pick<RepoStore, 'setFocus'>
  onSelect: (selectedOption: ComboboxOption) => unknown
}) => {
  const tabsStore = useTabsStoreRoot()
  const { encodedSegment } = useBranchOrCommitSegment()
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const editorMode = useEditorMode()
  const isDisabled = editorMode !== 'write:all'

  if (search && !multiple) return null

  if (!targetBlocks.length) {
    if (search) return null

    return (
      <div className="flex items-center gap-x-2 leading-none absolute text-dark-muted">
        <ArrowTopRightIcon />
        <span>{emptyStateLabel}</span>
      </div>
    )
  }

  if (targetBlocks.length === 1 && !multiple) {
    const [targetBlock] = targetBlocks
    if (!targetBlock) return null
    let blockTab: string | null
    let focusType: RepoStoreFocusType
    if (isBlockWithChildren(targetBlock)) {
      blockTab = idToSourceBlockId(targetBlock)
      focusType = 'block:document-title'
    } else {
      blockTab =
        getNode?.(targetBlock!.id, { required: true }).parentKey ?? null
      focusType = 'block:wrapper'
    }
    if (!blockTab) return null

    return (
      <ButtonLink
        unstyled
        href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${blockTab}`}
        className="flex items-center absolute space-x-2 hover:underline leading-none pr-1.5 overflow-hidden whitespace-nowrap max-w-[80%]"
        onClick={() => {
          tabsStore.getState().addTab({ key: blockTab, partiallyOpen: true })
          repoStore.setFocus({
            type: focusType,
            blockId: idToSourceBlockId(targetBlock),
          })
        }}
      >
        <span className="relative">
          <BlockIcon
            block={targetBlock}
            iconProps={{ className: 'text-dark-muted' }}
          />
          <span className="absolute -bottom-0.5 -right-1">
            <ArrowTopRightSmallIcon className="text-dark-muted" />
          </span>
        </span>
        <span className="text-ellipsis overflow-hidden">
          {targetBlock.title || 'Untitled'}
        </span>
      </ButtonLink>
    )
  }

  return targetBlocks.map((_block, index) => {
    let blockTab: string | null
    let focusType: RepoStoreFocusType
    if (isBlockWithChildren(_block)) {
      blockTab = idToSourceBlockId(_block)
      focusType = 'block:document-title'
    } else {
      blockTab = getNode?.(_block!.id, { required: true }).parentKey ?? null
      focusType = 'block:wrapper'
    }
    if (!blockTab) return null

    return (
      <div key={index} className="relative mr-6">
        <ButtonLink
          unstyled
          href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${blockTab}`}
          className="flex items-center space-x-2 hover:underline leading-none pr-1.5"
          onClick={() => {
            tabsStore.getState().addTab({ key: blockTab, partiallyOpen: true })
            repoStore.setFocus({
              type: focusType,
              blockId: idToSourceBlockId(_block),
            })
          }}
        >
          <span className="relative">
            <BlockIcon
              block={_block}
              iconProps={{ className: 'text-dark-muted' }}
            />
            <span className="absolute -bottom-0.5 -right-1">
              <ArrowTopRightSmallIcon className="text-dark-muted" />
            </span>
          </span>
          <span>{_block.title || 'Untitled'}</span>
        </ButtonLink>

        {multiple && !isDisabled ? (
          <IconButton
            className="absolute -right-5 top-1/2 -translate-y-1/2 cursor-default enabled:hover:bg-dark-shade-hover rounded-sm disabled:invisible disabled:pointer-events-none p-0.5"
            onClick={(e) => {
              e.stopPropagation()
              onSelect({
                label: _block.title,
                value: _block.id,
              })
            }}
          >
            <Cross2Icon className="text-dark-faint" width={14} height={14} />
          </IconButton>
        ) : (
          <div className="absolute -right-3.5 top-1/2 -translate-y-1/2 text-dark-faint h-[14px] w-[14px] text-lg flex items-center justify-center">
            -
          </div>
        )}
      </div>
    )
  })
}

export default Selector
